import React from 'react';

import { Link as RouterLink } from 'react-router-dom';

import {
  Box,
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { CheckCircle2 } from 'lucide-react';

import Reveal from '../../Components/Reveal';

import ImageCard1 from '../../Assets/RegisterProfessionalImages/1.png';
import ImageCard2 from '../../Assets/RegisterProfessionalImages/2.png';
import ImageCard3 from '../../Assets/RegisterProfessionalImages/3.png';
import ImageCard4 from '../../Assets/RegisterProfessionalImages/4.png';
import Random from '../../Assets/Random/random2.png';
import { COLORS } from '../../Constant/COLORS';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';
import ImageCard from './Components/ImageCard';
import CustomListItem from './Components/CustomListItem';
import { TypoPoppins } from '../../Components/Typo';
import FooterGetToKnow from '../../Components/FooterGetToKnow';

const MAX_WIDTH_OUTER_CONTAINERS = '80%';

const RegisterProfessional = () => {
  return (
    <>
      {/*Professional Page and RegisterProfessionalHome Page has same navbar look*/}
      <Header isRegisterProfessionalPage />
      <Box bgcolor={COLORS.blue}>
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          py={6}
          maxWidth={MAX_WIDTH_OUTER_CONTAINERS}
          mx='auto'
          justifyContent='space-between'
          alignItems='center'
          gap={{ xs: 3, md: 6 }}
        >
          <Box>
            <Typography
              variant='overline'
              sx={{ color: 'rgba(255,255,255,0.8)', letterSpacing: '2.5px', display: 'block', mb: 1.5 }}
              textAlign={{ xs: 'center', md: 'left' }}
            >
              Become an Evento partner
            </Typography>
            <Typography
              variant='h1'
              sx={{ color: '#fff', maxWidth: 540 }}
              mx={{ xs: 'auto', md: 'unset' }}
              textAlign={{ xs: 'center', md: 'left' }}
            >
              List your{' '}
              <Box component='span' sx={{ color: COLORS.blue2 }}>
                work
              </Box>{' '}
              on Evento
            </Typography>

            <TypoPoppins
              color='white'
              mt={{ xs: 3, md: 4 }}
              maxWidth={460}
              mx={{ xs: 'auto', md: 'unset' }}
              textAlign={{ xs: 'center', md: 'left' }}
            >
              Registration takes as little as 15 minutes to complete — get started
              today and reach thousands of event planners.
            </TypoPoppins>

            <Stack
              direction='row'
              alignItems='center'
              gap={1.5}
              mt={{ xs: 3, md: 5 }}
              justifyContent={{ xs: 'center', md: 'flex-start' }}
            >
              <TypoPoppins color='rgba(255,255,255,0.85)'>
                Already have an account?
              </TypoPoppins>
              <Button
                color='white1'
                component={RouterLink}
                to='/professional/login'
                sx={{ color: COLORS.teal, fontWeight: 600, px: 3 }}
              >
                Login
              </Button>
            </Stack>
          </Box>
          <Box width={{ xs: '100%', sm: 420 }} maxWidth='100%'>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                boxShadow: '0 20px 50px rgba(1,42,52,0.22)',
              }}
            >
              <Typography variant='h5' mb={2}>
                Create a new listing
              </Typography>
              <List sx={{ ' > *:not(:last-child)': { pb: 0.5 } }}>
                {[
                  'Incredible choice',
                  'Affordable rates',
                  'Instant confirmation',
                  'Secure booking',
                  '24/7 availability',
                ].map(item => (
                  <ListItem key={item} disablePadding>
                    <ListItemIcon sx={{ minWidth: 38 }}>
                      <CheckCircle2 color={COLORS.teal} strokeWidth={1.8} size={22} />
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ color: COLORS.ink, fontWeight: 500 }}
                    >
                      {item}
                    </ListItemText>
                  </ListItem>
                ))}
              </List>
              <Typography color={COLORS.inkSoft} mt={2}>
                Create a partner account to get started
              </Typography>
              <Button
                color='blue'
                fullWidth
                size='large'
                component={RouterLink}
                to='/professional/registration'
                sx={{ mt: 3, color: '#fff' }}
              >
                Get Started
              </Button>
            </Box>
            <TypoPoppins
              color='white'
              mt={3}
              textAlign={{ xs: 'center', md: 'unset' }}
            >
              By continuing, you agree to let Evento.lk email you regarding your
              registration.
            </TypoPoppins>
          </Box>
        </Stack>
      </Box>
      <Box maxWidth={MAX_WIDTH_OUTER_CONTAINERS} mx='auto'>
        <Reveal>
          <Typography
            variant='overline'
            color={COLORS.teal}
            display='block'
            mt={{ xs: 4, md: 7 }}
            textAlign={{ xs: 'center', md: 'left' }}
          >
            Why partner with us
          </Typography>
          <Typography variant='h3' mb={{ xs: 4, md: 5 }} textAlign={{ xs: 'center', md: 'left' }}>
            Benefits of working with us
          </Typography>
        </Reveal>
        <Grid
          container
          rowSpacing={5}
          maxWidth='90%'
          columnSpacing={20}
          textAlign={{ xs: 'center', md: 'unset' }}
        >
          <Grid item lg={6}>
            <ImageCard
              title='List any type of service'
              desc='From event decoration to cake making and everything in between can be listed.'
              src={ImageCard3}
            />
          </Grid>
          <Grid item lg={6}>
            <ImageCard
              title='Step-by-step guidance'
              desc='You’ll learn how our platform works, best practices, and things to watch out for.'
              src={ImageCard2}
            />
          </Grid>
          <Grid item lg={6}>
            <ImageCard
              title='Easily import details'
              desc='To save you time, many of the details from your existing listings can be imported.'
              src={ImageCard1}
            />
          </Grid>
          <Grid item lg={6}>
            <ImageCard
              title='Affordable rates'
              desc='We guarantee to offer you the best available rates. And with our promise to price match you can get assured that you are always getting a great deal'
              src={ImageCard4}
            />
          </Grid>
        </Grid>
      </Box>
      <Box bgcolor={COLORS.white2} mt={2} pt={5}>
        <Box maxWidth={MAX_WIDTH_OUTER_CONTAINERS} mx='auto'>
          <Typography variant='overline' color={COLORS.teal} display='block'>
            How it works
          </Typography>
          <Typography variant='h3'>Listing your service</Typography>
          <TypoPoppins color={COLORS.inkSoft} mt={2} maxWidth={560}>
            Never listed your service before? Here’s an overview of the steps
            we’ll guide you through to create your listing.
          </TypoPoppins>
          <Stack direction={{ xs: 'column-reverse', md: 'unset' }} mt={3}>
            <List
              sx={{
                '&>*': { mb: 5 },
                position: 'relative',
                whiteSpace: { lg: 'nowrap' },
              }}
            >
              <Divider
                orientation='horizontal'
                style={{
                  height: 'calc(100% - 150px)',
                  position: 'absolute',
                  top: '43%',
                  borderWidth: '4px',
                  transform: 'translate(-50%,-50%)',
                  borderColor: COLORS.blue,
                  zIndex: '0',
                  left: '27.5px',
                }}
              />
              <CustomListItem
                listIndex={1}
                title='Name and location'
                desc='Give your name and address'
              />
              <CustomListItem
                listIndex={2}
                title='Photos'
                desc='Add at least three high-quality photo of your product to service.'
              />
              <CustomListItem
                listIndex={3}
                title='Price rates'
                desc='Set the prices for your products and services'
              />
              <CustomListItem
                listIndex={4}
                title='Review'
                desc=' Review your details and open your product/ service to your customers'
              />
            </List>

            <Box width='100%' height='100%'>
              <img
                src={Random}
                alt=''
                style={{
                  width: '100%',
                  objectFit: 'cover',
                  height: '100%',
                  objectPosition: 'right',
                }}
              />
            </Box>
          </Stack>
        </Box>

        <FooterGetToKnow />
      </Box>
      <Footer isRegisterProfessionalPage />
    </>
  );
};

export default RegisterProfessional;
